"use client";

import { cn } from "@/lib/utils";
import { CertificateSectionProps } from "./types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { memo, useState } from "react";
import { LayoutGroup, motion } from "framer-motion";
import Icon from "@/components/ui/Icon";
import { TranslationCard } from "@/components/screen/global-components/translation";
import { Typography } from "@/components/ui/typography";
import ArrowLink from "@/components/ui/arrow-link";
import Image from "next/image";

const ActiveAccordionIndicator: React.FC<React.ComponentProps<"span">> = ({
  className,
}) => {
  return (
    <motion.span
      layoutId="activeAccordionId"
      transition={{
        duration: 0.2,
      }}
      className={cn("w-0.5 h-full absolute -left-px top-0 bg-blue", className)}
      aria-hidden="true"
      role="presentation"
    />
  );
};

const CertificateSection = ({
  title,
  description,
  items,
  card,
  imageUrl,
  imageDimensions,
  variantSecondary = false,
  accordionMultiple = false,
  className,
}: CertificateSectionProps) => {
  const [activeAccordion, setActiveAccordion] = useState<string[] | string>(
    accordionMultiple ? [items[0]?.id] : items[0]?.id ?? ""
  );

  return (
    <section
      className={cn(
        "section bg-background-secondary lg:bg-transparent",
        className
      )}
    >
      <div className="container mx-auto py-12 lg:py-20">
        {!!description && (
          <div className="grid grid-cols-1 gap-4 lg:gap-2 md:text-center md:max-w-[840] md:mx-auto">
            <Typography variant="heading4" asChild>
              <h2>{title}</h2>
            </Typography>
            <Typography variant="subHeadingSm" asChild>
              <p>{description}</p>
            </Typography>
          </div>
        )}
        <div
          className={cn("grid grid-cols-1 gap-6 place-content-start", {
            "md:grid-cols-2 md:gap-8": variantSecondary,
            "md:grid-cols-[minmax(0,0.581fr)_minmax(0,0.419fr)] md:gap-[min(3rem,3.33vw)]":
              !variantSecondary,
            "mt-6 lg:mt-12": !!description,
          })}
        >
          <div className={cn({ "order-2 md:order-1": variantSecondary })}>
            {!description && (
              <Typography
                variant="heading4"
                className="text-center md:text-left md:mb-4"
                asChild
              >
                <h2>{title}</h2>
              </Typography>
            )}
            <div
              className={cn("relative border-l border-l-blue-200", {
                "md:border-l-4 md:border-l-[#eaeaea]": variantSecondary,
                "hidden md:block": !variantSecondary,
              })}
            >
              <LayoutGroup>
                <Accordion
                  {...(accordionMultiple
                    ? { type: "multiple" }
                    : { type: "single", collapsible: true })}
                  value={activeAccordion as any}
                  onValueChange={setActiveAccordion as any}
                >
                  {items?.map(
                    ({ id, title, description, list, linkText, link }) => {
                      return (
                        <AccordionItem
                          key={id}
                          value={id}
                          className={cn("group relative pb-1 border-0 !m-0", {
                            "before:absolute before:left-0 before:top-0 before:h-full before:border-l-[2px] before:border-l-blue before:opacity-0 before:transition-opacity data-[state=open]:before:opacity-100":
                              accordionMultiple,
                          })}
                        >
                          <AccordionTrigger
                            className={cn(
                              "p-4 pb-3 text-base text-gray-800 cursor-pointer font-semibold hover:no-underline leading-[150%] flex justify-between",
                              {
                                "[&>svg]:!hidden text-lg leading-[130%] font-medium text-semantic-text-primary will-change-colors transition-colors group-data-[state=open]:text-blue lg:text-gray-800 lg:text-xl lg:leading-[150%] lg:font-semibold md:pl-3":
                                  variantSecondary,
                              }
                            )}
                          >
                            <span>{title}</span>
                          </AccordionTrigger>
                          <AccordionContent
                            className={cn("px-4 pb-3", {
                              "md:pl-3": variantSecondary,
                            })}
                          >
                            {!!description && (
                              <p className="text-gray-600 text-sm leading-[150%]">
                                {description}
                              </p>
                            )}
                            {!!list?.length && (
                              <ul className="mt-[5px] grid grid-cols-1 gap-3 text-base text-gray-600 leading-[130%] text-semantic-text-primary lg:text-gray-800 lg:text-sm lg:leading-[150%]">
                                {list.map((item, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center gap-2"
                                  >
                                    <Icon
                                      id="icon-check_24"
                                      width={24}
                                      height={24}
                                      className="shrink-0 w-6 h-6 text-blue-500 lg:hidden"
                                    />
                                    <Icon
                                      id="icon-circle-check_20"
                                      width={20}
                                      height={20}
                                      className="shrink-0 w-5 h-5 text-gray-800 hidden lg:block"
                                    />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {!!linkText && (
                              <div className="mt-4 flex">
                                <ArrowLink href={link ?? "#"}>
                                  {linkText}
                                </ArrowLink>
                              </div>
                            )}
                          </AccordionContent>
                          {activeAccordion === id && !accordionMultiple && (
                            <ActiveAccordionIndicator
                              className={cn({
                                "md:w-1 md:-left-1": variantSecondary,
                              })}
                            />
                          )}
                        </AccordionItem>
                      );
                    }
                  )}
                </Accordion>
              </LayoutGroup>
            </div>
          </div>
          {!!card && (
            <div>
              <TranslationCard
                iconId={card.iconId}
                title={card.title}
                priceLabel={card.priceLabel}
                buttonPrimaryLabel={card.buttonPrimaryLabel}
                buttonPrimaryHref={card.buttonPrimaryHref}
                inheritsFrom={card.inheritsFrom}
                features={card.features}
                notice={card?.notice}
              />
            </div>
          )}
          {!!imageUrl && (
            <div
              className={cn("flex", { "order-1 md:order-2": variantSecondary })}
            >
              <div className="flex-auto min-w-0 rounded-3xl flex overflow-hidden inline-flex">
                <Image
                  src={imageUrl}
                  width={imageDimensions?.width}
                  height={imageDimensions?.height}
                  alt={title}
                  className="flex-auto min-w-0"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default memo(CertificateSection);
